import { Injectable, OnDestroy } from '@angular/core';
import { WebSocketProvider } from './web-socket.provider';
import { Observable, Subject, lastValueFrom, tap, takeUntil } from 'rxjs';
import { CommonConstant } from '../constants';
import {
  POSOperatorMessage,
  POSOperatorMessageEvent
} from '../models/pos-operator-websocket.model';
import {
  WSCreatePOSOperator,
  WSUpdatePOSOperator,
  WSDeletePOSOperator,
  CompleteDeviceRegistration,
  UpdatedDevice,
  ResetApplication
} from '../store/app/app.action';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';

@Injectable({
  providedIn: 'root'
})
export class PosOperatorProvider implements OnDestroy {
  private _posOperatorActivityChannelSubscription: Observable<any>;
  private _isInit = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _webSocketProvider: WebSocketProvider,
    private readonly _store: Store,
    private readonly _actions$: Actions
  ) {}

  init() {
    if (this._isInit) {
      return;
    }

    this._actions$
      .pipe(
        takeUntil(this._destroy$),
        ofActionDispatched(CompleteDeviceRegistration)
      )
      .subscribe(() => this._initPosOperatorActivityChannelSubscription());

    this._actions$
      .pipe(
        takeUntil(this._destroy$),
        ofActionDispatched(UpdatedDevice, ResetApplication)
      )
      .subscribe(() => this._resetPosOperatorActivityChannelSubscription());

    this._isInit = true;
  }

  /** Re-subscribe to OperatorChannel. Called after WS reconnect (e.g., training mode toggle). */
  initOperatorChannelSubscription() {
    this._initPosOperatorActivityChannelSubscription();
  }

  /** Unsubscribe from OperatorChannel. Called before WS disconnect (e.g., training mode toggle). */
  resetOperatorChannelSubscription() {
    this._resetPosOperatorActivityChannelSubscription();
  }

  private _initPosOperatorActivityChannelSubscription() {
    if (!!this._posOperatorActivityChannelSubscription) {
      return;
    }

    this._posOperatorActivityChannelSubscription =
      this._subscribePosOperatorActivityChannel();
    this._getDataFromPosOperatorActivityChannel();
  }

  private _resetPosOperatorActivityChannelSubscription() {
    this._posOperatorActivityChannelSubscription = null;
    this._webSocketProvider.unsubscribe(
      CommonConstant.WEBSOCKET_CHANNELS.POS_OPERATOR_CHANNEL
    );
  }

  private _subscribePosOperatorActivityChannel() {
    if (this._posOperatorActivityChannelSubscription) {
      this._resetPosOperatorActivityChannelSubscription();
    }

    return this._webSocketProvider.subscribeTo(
      CommonConstant.WEBSOCKET_CHANNELS.POS_OPERATOR_CHANNEL
    );
  }

  private _getDataFromPosOperatorActivityChannel() {
    if (!this._posOperatorActivityChannelSubscription) {
      return;
    }

    return lastValueFrom(
      this._posOperatorActivityChannelSubscription.pipe(
        tap((response) => {
          try {
            this._getWsMessageActions(response);
          } catch (e) {
            console.error('POS Operator WS message processing error', e);
          }
        })
      )
    );
  }

  private _getWsMessageActions(message: POSOperatorMessage) {
    switch (message.event) {
      case POSOperatorMessageEvent.POS_OPERATOR_CREATED:
        return this._store.dispatch(new WSCreatePOSOperator(message.data));
      case POSOperatorMessageEvent.POS_OPERATOR_UPDATED:
        return this._store.dispatch(new WSUpdatePOSOperator(message.data));
      case POSOperatorMessageEvent.POS_OPERATOR_DELETED:
        return this._store.dispatch(
          new WSDeletePOSOperator(message.data.pos_operator_id)
        );
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
